import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Archive, Upload, Download, Trash2, Search, FileText, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/utils/dates";

export default function ContractsArchivePage() {
  const { axiosAuth } = useAuth();
  const { settings } = useSettings();
  const accent = settings?.primary_color || "#DC2626";
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [rentals, setRentals] = useState([]);
  const [uploadForm, setUploadForm] = useState({ rental_id: "", notes: "", file_data: "", file_name: "" });
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const api = axiosAuth();
      const [cRes, rRes] = await Promise.all([
        api.get("/contracts-archive"),
        api.get("/rentals")
      ]);
      setContracts(cRes.data);
      setRentals(rRes.data);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Solo file PDF"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(",")[1];
      setUploadForm(prev => ({ ...prev, file_data: b64, file_name: file.name }));
      toast.success(`File selezionato: ${file.name}`);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!uploadForm.file_data) { toast.error("Seleziona un file PDF"); return; }
    setUploading(true);
    try {
      const api = axiosAuth();
      await api.post("/contracts-archive", {
        rental_id: uploadForm.rental_id || null,
        file_data: uploadForm.file_data,
        file_name: uploadForm.file_name,
        source: "upload",
        notes: uploadForm.notes
      });
      toast.success("Contratto archiviato!");
      setUploadDialog(false);
      setUploadForm({ rental_id: "", notes: "", file_data: "", file_name: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setUploading(false); }
  };

  const archiveDigitalContract = async (rentalId) => {
    try {
      const api = axiosAuth();
      const res = await api.get(`/rentals/${rentalId}/contract`, { responseType: "blob" });
      const b64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(res.data);
      });
      const rental = rentals.find(r => r.id === rentalId);
      const fname = `Contratto_${rental?.contract_number?.replace("/", "_") || rentalId.slice(0, 8)}_${rental?.client?.surname || ""}_${rental?.vehicle?.model || ""}.pdf`;
      await api.post("/contracts-archive", {
        rental_id: rentalId,
        file_data: b64,
        file_name: fname,
        source: "digital",
        notes: "Contratto firmato digitalmente"
      });
      toast.success("Contratto firmato archiviato!");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore archiviazione"); }
  };

  const downloadContract = async (contractId) => {
    try {
      const api = axiosAuth();
      const res = await api.get(`/contracts-archive/${contractId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      const contract = contracts.find(c => c.id === contractId);
      a.download = contract?.file_name || "contratto.pdf";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
  };

  const handleDelete = async () => {
    try {
      const api = axiosAuth();
      await api.delete(`/contracts-archive/${deleteDialog.id}`);
      toast.success("Eliminato");
      setDeleteDialog(null);
      fetchData();
    } catch { toast.error("Errore"); }
  };

  const filtered = contracts.filter(c => {
    const q = search.toLowerCase();
    return `${c.contract_number || ""} ${c.client?.name || ""} ${c.client?.surname || ""} ${c.vehicle?.brand || ""} ${c.vehicle?.model || ""} ${c.file_name || ""} ${c.notes || ""}`.toLowerCase().includes(q);
  });

  const activeRentalsWithContract = rentals.filter(r => r.contract_number);

  return (
    <div className="space-y-6" data-testid="contracts-archive-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Archive className="w-5 h-5" /> Archivio Contratti
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{contracts.length} contratti archiviati</p>
        </div>
        <Button onClick={() => { setUploadDialog(true); setUploadForm({ rental_id: "", notes: "", file_data: "", file_name: "" }); }} style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg shadow-sm" data-testid="archive-upload-button">
          <Plus className="w-4 h-4 mr-2" /> Archivia Contratto
        </Button>
      </div>

      {/* Quick archive from existing contracts */}
      {activeRentalsWithContract.length > 0 && (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
          <CardHeader className="pb-2">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Archivia contratto firmato digitalmente</p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {activeRentalsWithContract.slice(0, 10).map(r => {
                const alreadyArchived = contracts.some(c => c.rental_id === r.id);
                return (
                  <Button key={r.id} variant="outline" size="sm" disabled={alreadyArchived}
                    onClick={() => archiveDigitalContract(r.id)}
                    className={`text-xs rounded-lg ${alreadyArchived ? "opacity-50" : ""}`}
                    data-testid={`archive-rental-${r.id}`}>
                    <FileText className="w-3 h-3 mr-1" />
                    #{r.contract_number} - {r.client?.surname} {r.vehicle?.model}
                    {alreadyArchived && <Badge className="ml-1 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-[10px]">Archiviato</Badge>}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Archive Table */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
        <CardHeader className="pb-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Cerca contratto..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 rounded-lg" data-testid="archive-search" />
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50 dark:bg-slate-800/50">
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200">Contratto</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200">Cliente</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden md:table-cell">Veicolo</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden md:table-cell">Tipo</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden lg:table-cell">Data</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 hidden lg:table-cell">Note</TableHead>
                <TableHead className="font-semibold text-slate-700 dark:text-slate-200 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Nessun contratto archiviato</TableCell></TableRow>
              ) : (
                filtered.map(c => (
                  <TableRow key={c.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50" data-testid={`archive-row-${c.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-red-500 shrink-0" />
                        <div>
                          <p className="font-medium text-sm text-slate-900 dark:text-white">{c.contract_number || "Manuale"}</p>
                          <p className="text-[10px] text-slate-400 truncate max-w-[150px]">{c.file_name}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700 dark:text-slate-200">{c.client ? `${c.client.name} ${c.client.surname}` : "-"}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-600 dark:text-slate-300">{c.vehicle ? `${c.vehicle.brand} ${c.vehicle.model}` : "-"}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge className={c.source === "digital" ? "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700 text-xs" : "bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700 text-xs"}>
                        {c.source === "digital" ? "Digitale" : "Caricato"}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-500 dark:text-slate-400">{formatDate(c.created_at?.split("T")[0])}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-slate-400 dark:text-slate-500 truncate max-w-[120px]">{c.notes || "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button onClick={() => downloadContract(c.id)} className="h-7 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-md" data-testid={`download-archive-${c.id}`}>
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteDialog(c)} className="h-7 w-7 text-slate-500 hover:text-red-600 dark:text-slate-400" data-testid={`delete-archive-${c.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="sm:max-w-lg rounded-xl bg-white dark:bg-slate-900" data-testid="archive-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-slate-900 dark:text-white">Archivia Contratto</DialogTitle>
            <DialogDescription>Carica un contratto PDF firmato per archiviarlo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" data-testid="archive-file-input" />
              <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="w-full h-16 border-dashed border-2 border-slate-300 dark:border-slate-600 rounded-lg" data-testid="archive-file-button">
                <Upload className="w-5 h-5 mr-2" />
                {uploadForm.file_name ? uploadForm.file_name : "Seleziona file PDF"}
              </Button>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-200">Collega a Noleggio (opzionale)</Label>
              <Select value={uploadForm.rental_id} onValueChange={(v) => setUploadForm(prev => ({ ...prev, rental_id: v }))}>
                <SelectTrigger className="h-10 rounded-lg"><SelectValue placeholder="Nessun collegamento" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun collegamento</SelectItem>
                  {activeRentalsWithContract.map(r => (
                    <SelectItem key={r.id} value={r.id}>#{r.contract_number} - {r.client?.surname} {r.vehicle?.model}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-200">Note</Label>
              <Input value={uploadForm.notes} onChange={(e) => setUploadForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Note opzionali..." className="h-10 rounded-lg" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)} className="rounded-lg">Annulla</Button>
            <Button onClick={handleUpload} disabled={!uploadForm.file_data || uploading} style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg" data-testid="archive-save-button">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvataggio...</> : "Archivia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-md rounded-xl bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle>Elimina Contratto</DialogTitle>
            <DialogDescription>Sei sicuro di voler eliminare "{deleteDialog?.file_name}"? Il file verra rimosso dall'archivio.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="rounded-lg" data-testid="cancel-delete-archive">Annulla</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="confirm-delete-archive">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
